// Hand-rolled spreadsheet selection engine for the SmartCharger "Manage" table.
//
// Deliberately NOT a datagrid library (decision D12) — it layers a rectangular
// cell-selection + keyboard model ON TOP of the existing InlineEditField cells,
// coordinating around them rather than replacing them:
//   • click a cell to select; shift+click / shift+arrows to extend a range
//   • arrow keys move the active cell (when NOT editing an input)
//   • Ctrl/Cmd+D  fill down  — the top row's value → the rest of each column
//   • Ctrl/Cmd+C  copy       — range → internal clipboard (+ TSV to system)
//   • Ctrl/Cmd+V  paste      — clipboard → same columns from the active cell
//   • Escape clears the selection
//
// Fill/copy/paste stay WITHIN a column's value domain (same-column semantics),
// so a select/checkbox value is never pasted into an incompatible column.
// Individual-cell EDITING remains InlineEditField's click-to-edit (v1); the
// keydown handler bails while focus is in an input/select/textarea so it never
// fights the editor.

import { useCallback, useRef, useState } from "react";

export interface SheetCol {
	key: string;
	editable: boolean;
}

export interface SheetCellRef {
	ri: number;
	ci: number;
	sheet: SheetApi;
}

export interface UseSheetOpts {
	rowCount: number;
	cols: SheetCol[];
	// Current value of a cell as a string ('' = empty; 'true'/'false' for checkboxes).
	readCell: (r: number, c: number) => string;
	// Commit a new string value to a cell (parses per column type downstream).
	writeCell: (r: number, c: number, value: string) => Promise<void> | void;
	// Focused when the selection changes, so arrow keys work right after a click.
	containerRef: React.RefObject<HTMLElement>;
}

export interface SheetApi {
	isSelected: (r: number, c: number) => boolean;
	isActive: (r: number, c: number) => boolean;
	selectCell: (r: number, c: number, extend?: boolean, focus?: boolean) => void;
	onKeyDown: (e: React.KeyboardEvent) => void;
	fillDown: () => void;
	copy: () => void;
	paste: () => void;
	clear: () => void;
	// Number of cells currently selected (0 when nothing is selected).
	selectedCount: number;
	hasClipboard: boolean;
	busy: boolean;
	// Selection geometry (0-based, inclusive) for the caller to render a highlight
	// rectangle via generated CSS; null when nothing is selected.
	range: { r0: number; c0: number; r1: number; c1: number } | null;
	active: { r: number; c: number } | null;
}

interface Sel {
	a: { r: number; c: number }; // anchor
	f: { r: number; c: number }; // focus (the active cell)
}

const clamp = (n: number, max: number) => Math.max(0, Math.min(max, n));

export function useSheetSelection(opts: UseSheetOpts): SheetApi {
	const { rowCount, cols, readCell, writeCell, containerRef } = opts;
	const colCount = cols.length;
	const [sel, setSel] = useState<Sel | null>(null);
	const [busy, setBusy] = useState(false);
	const clipRef = useRef<{ values: string[][] } | null>(null);

	// Keep the latest data-access closures in a ref so the memoized handlers
	// below don't need to be recreated (and re-bound) on every render.
	const accessRef = useRef({ readCell, writeCell, cols, rowCount, colCount });
	accessRef.current = { readCell, writeCell, cols, rowCount, colCount };

	const bounds = (s: Sel) => ({
		r0: Math.min(s.a.r, s.f.r),
		r1: Math.max(s.a.r, s.f.r),
		c0: Math.min(s.a.c, s.f.c),
		c1: Math.max(s.a.c, s.f.c),
	});

	const focusGrid = useCallback(() => {
		// Defer so the click that triggered selection finishes first.
		requestAnimationFrame(() => containerRef.current?.focus());
	}, [containerRef]);

	const selectCell = useCallback(
		(r: number, c: number, extend = false, focus = true) => {
			const { rowCount: rc, colCount: cc } = accessRef.current;
			const f = { r: clamp(r, rc - 1), c: clamp(c, cc - 1) };
			setSel((prev) => (extend && prev ? { a: prev.a, f } : { a: f, f }));
			if (focus) focusGrid();
		},
		[focusGrid],
	);

	const move = useCallback((dr: number, dc: number, extend: boolean) => {
		const { rowCount: rc, colCount: cc } = accessRef.current;
		setSel((prev) => {
			const base = prev ? prev.f : { r: 0, c: 0 };
			const f = { r: clamp(base.r + dr, rc - 1), c: clamp(base.c + dc, cc - 1) };
			return extend && prev ? { a: prev.a, f } : { a: f, f };
		});
	}, []);

	const clear = useCallback(() => setSel(null), []);

	const runWrites = useCallback(async (tasks: Array<Promise<void> | void>) => {
		if (!tasks.length) return;
		setBusy(true);
		try {
			await Promise.allSettled(tasks);
		} finally {
			setBusy(false);
		}
	}, []);

	const fillDown = useCallback(() => {
		setSel((s) => {
			if (!s) return s;
			const { readCell: rd, writeCell: wr, cols: cl } = accessRef.current;
			const b = bounds(s);
			const tasks: Array<Promise<void> | void> = [];
			for (let c = b.c0; c <= b.c1; c++) {
				if (!cl[c]?.editable) continue;
				const top = rd(b.r0, c);
				for (let r = b.r0 + 1; r <= b.r1; r++) {
					if (rd(r, c) !== top) tasks.push(wr(r, c, top));
				}
			}
			void runWrites(tasks);
			return s;
		});
	}, [runWrites]);

	const copy = useCallback(() => {
		setSel((s) => {
			if (!s) return s;
			const { readCell: rd } = accessRef.current;
			const b = bounds(s);
			const values: string[][] = [];
			for (let r = b.r0; r <= b.r1; r++) {
				const row: string[] = [];
				for (let c = b.c0; c <= b.c1; c++) row.push(rd(r, c));
				values.push(row);
			}
			clipRef.current = { values };
			try {
				navigator.clipboard?.writeText(values.map((row) => row.join("\t")).join("\n"));
			} catch {
				/* system clipboard is best-effort; the internal one is what paste uses */
			}
			return s;
		});
	}, []);

	const paste = useCallback(() => {
		const clip = clipRef.current;
		if (!clip) return;
		setSel((s) => {
			if (!s) return s;
			const { readCell: rd, writeCell: wr, cols: cl, rowCount: rc, colCount: cc } = accessRef.current;
			const startR = Math.min(s.a.r, s.f.r);
			const startC = Math.min(s.a.c, s.f.c);
			const tasks: Array<Promise<void> | void> = [];
			clip.values.forEach((row, dr) =>
				row.forEach((val, dc) => {
					const r = startR + dr;
					const c = startC + dc;
					if (r >= rc || c >= cc || !cl[c]?.editable) return;
					if (rd(r, c) !== val) tasks.push(wr(r, c, val));
				}),
			);
			void runWrites(tasks);
			return s;
		});
	}, [runWrites]);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			const tag = (e.target as HTMLElement)?.tagName;
			// Editing — let InlineEditField's own input/select handle the keys.
			if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
			const meta = e.metaKey || e.ctrlKey;
			if (meta) {
				const k = e.key.toLowerCase();
				if (k === "d") { e.preventDefault(); fillDown(); return; }
				if (k === "c") { e.preventDefault(); copy(); return; }
				if (k === "v") { e.preventDefault(); paste(); return; }
				return;
			}
			switch (e.key) {
				case "ArrowUp": e.preventDefault(); move(-1, 0, e.shiftKey); break;
				case "ArrowDown": e.preventDefault(); move(1, 0, e.shiftKey); break;
				case "ArrowLeft": e.preventDefault(); move(0, -1, e.shiftKey); break;
				case "ArrowRight": e.preventDefault(); move(0, 1, e.shiftKey); break;
				case "Escape": setSel(null); break;
				default: break;
			}
		},
		[fillDown, copy, paste, move],
	);

	let selectedCount = 0;
	if (sel) {
		const b = bounds(sel);
		selectedCount = (b.r1 - b.r0 + 1) * (b.c1 - b.c0 + 1);
	}

	const api: SheetApi = {
		isSelected: (r, c) => {
			if (!sel) return false;
			const b = bounds(sel);
			return r >= b.r0 && r <= b.r1 && c >= b.c0 && c <= b.c1;
		},
		isActive: (r, c) => !!sel && sel.f.r === r && sel.f.c === c,
		selectCell,
		onKeyDown,
		fillDown,
		copy,
		paste,
		clear,
		selectedCount,
		hasClipboard: !!clipRef.current,
		busy,
		range: sel ? bounds(sel) : null,
		active: sel ? { r: sel.f.r, c: sel.f.c } : null,
	};
	return api;
}
